// Work-day helpers for the Order Duration schedule.
// Weekend = Saturday (6) or Sunday (0). No holiday calendar.

export function isWeekend(d: Date): boolean {
  const w = d.getDay();
  return w === 0 || w === 6;
}

export function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function addCalendarDays(d: Date, days: number): Date {
  const r = startOfDay(d);
  r.setDate(r.getDate() + days);
  return r;
}

/** End date after adding N work days to start. 0 work days → same calendar day as start. */
export function addWorkdays(start: Date, days: number): Date {
  const d = startOfDay(start);
  if (!Number.isFinite(days) || days <= 0) return d;
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (!isWeekend(d)) added++;
  }
  return d;
}

/**
 * Count work days from start (exclusive) to end (inclusive). Same day → 0.
 * Mirror of addWorkdays: addWorkdays(start, countWorkdays(start, end)) == end for any end ≥ start
 * whose week-alignment matches addWorkdays's skip pattern.
 */
export function countWorkdays(start: Date, end: Date): number {
  const a = startOfDay(start);
  const b = startOfDay(end);
  if (b <= a) return 0;
  let count = 0;
  const d = new Date(a);
  while (d < b) {
    d.setDate(d.getDate() + 1);
    if (!isWeekend(d)) count++;
  }
  return count;
}

/** YYYY-MM-DD in local time — round-trippable through <DateInput>. */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as a local date (not UTC, avoids timezone-edge bugs). */
export function fromISODate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Differences in calendar days, inclusive of both endpoints for display. */
export function calendarDaysBetween(a: Date, b: Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / 86_400_000);
}
